<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { type MediaPreview } from '@/v2/blocks/response-block/helpers/media-types'
import { prettifyNdjson } from '@/v2/blocks/response-block/helpers/prettify-ndjson'
import { useLocalization } from '@/v2/features/localization'

import ResponseBodyInfo from './ResponseBodyInfo.vue'
import ResponseBodyRaw from './ResponseBodyRaw.vue'

const {
  src,
  type,
  mode,
  alpha = false,
  content,
} = defineProps<{
  src: string
  type: string
  mode: MediaPreview
  alpha?: boolean | undefined
  /** Decoded body; used when `mode` is `json` for pretty-printed preview (no JSON.parse round-trip). */
  content?: unknown
}>()

const { translate } = useLocalization()

const jsonPreviewContent = computed((): string => {
  const value = content
  if (typeof value === 'string') {
    return value
  }
  if (value == null) {
    return ''
  }
  return String(value)
})

/** NDJSON pretty-printed one record per block for the `ndjson` preview. */
const ndjsonPreviewContent = computed((): string =>
  prettifyNdjson(jsonPreviewContent.value),
)

/**
 * Validates the `src` against an allow-list of safe protocols before it is
 * passed to a rendering element. This blocks XSS vectors such as
 * `javascript:` and `vbscript:` URIs and also prevents accidentally embedding
 * a `data:text/html` payload that could execute script in the app origin.
 *
 * Allowed:
 *   - `blob:` (the standard case — generated client-side by `URL.createObjectURL`)
 *   - `http:` / `https:`
 *   - `data:` URIs whose MIME type is a known safe media kind
 *     (`image/*`, `video/*`, `audio/*`, `application/pdf`, `application/octet-stream`)
 *
 * Anything else (including malformed or relative URLs) collapses to an empty
 * string so the template falls back to the "Preview unavailable" message.
 */
const safeSrc = computed((): string => {
  if (!src) {
    return ''
  }

  if (/^data:/i.test(src)) {
    // The MIME type must be one of the known safe kinds AND be terminated by
    // `;` (parameters / `;base64`) or `,` (start of the payload). Anchoring
    // at the terminator prevents prefix-matching tricks like
    // `data:image/png-but-actually-html,…`.
    return /^data:(?:(?:image|video|audio)\/[a-z0-9.+-]+|application\/pdf|application\/octet-stream)[;,]/i.test(
      src,
    )
      ? src
      : ''
  }

  try {
    const parsed = new URL(src)
    if (
      parsed.protocol === 'blob:' ||
      parsed.protocol === 'http:' ||
      parsed.protocol === 'https:'
    ) {
      return src
    }
  } catch {
    // Malformed or relative URL — refuse to render.
  }

  return ''
})

const error = ref(false)

/**
 * Whether the embedded document is a PDF.
 *
 * PDFs are rendered by the browser's own PDF engine (e.g. PDFium), which runs
 * in an isolated context that cannot reach this app's origin — its DOM,
 * cookies or storage. A PDF's embedded JavaScript therefore cannot be used to
 * attack us, and a strict `sandbox=""` does not confine that engine; it only
 * prevents Chromium from loading the PDF viewer at all (it boots as a scripted
 * extension page), leaving the user with a blank frame. So PDFs are rendered
 * without the `sandbox` attribute, matching the original `<object>` behaviour.
 *
 * Everything else that reaches the iframe (notably `text/html`, the genuine
 * XSS vector) keeps the strict empty sandbox.
 */
const isPdf = computed(
  (): boolean => type.trim().toLowerCase() === 'application/pdf',
)

watch(
  () => src,
  () => (error.value = false),
)
</script>
<template>
  <ResponseBodyRaw
    v-if="mode === 'json'"
    :content="jsonPreviewContent"
    language="json"
    prettyPrintJson />
  <ResponseBodyRaw
    v-else-if="mode === 'ndjson'"
    :content="ndjsonPreviewContent"
    language="json" />
  <div
    v-else-if="!error && safeSrc"
    class="flex justify-center overflow-auto rounded-b"
    :class="{ 'bg-preview p-2': alpha }">
    <img
      v-if="mode === 'image'"
      class="h-full max-w-full"
      :class="{ rounded: alpha }"
      referrerpolicy="no-referrer"
      :src="safeSrc"
      @error="error = true" />
    <video
      v-else-if="mode === 'video'"
      autoplay
      controls
      referrerpolicy="no-referrer"
      width="100%"
      @error="error = true">
      <source
        :src="safeSrc"
        :type="type" />
    </video>
    <audio
      v-else-if="mode === 'audio'"
      class="my-12"
      controls
      referrerpolicy="no-referrer"
      @error="error = true">
      <source
        :src="safeSrc"
        :type="type" />
    </audio>
    <!--
      `<object>` would execute scripts inside the embedded document in the
      app's origin, so we render arbitrary previews inside an `<iframe>`
      instead. Non-PDF documents (notably `text/html`) get an empty `sandbox`
      attribute, which disables scripts, forms, popups and same-origin access.
      PDFs are exempted (see `isPdf` above): the empty sandbox does not confine
      the browser's PDF engine, it only stops the viewer from loading at all.

      Note: an `error` event on `<iframe>` is not reliably fired for failed
      navigations — browsers usually fire `load` even when the response is
      blank or an error page, and a cross-origin sandboxed frame cannot be
      introspected from JavaScript. There is therefore no useful runtime
      fallback for an unreachable URL; the URL allow-list above is what
      keeps unsafe content out in the first place.
    -->
    <iframe
      v-else
      class="aspect-[4/3] w-full border-0"
      referrerpolicy="no-referrer"
      :sandbox="isPdf ? undefined : ''"
      :src="safeSrc" />
  </div>
  <ResponseBodyInfo v-else>
    {{ translate('apiClient.responseBodyPreview.previewUnavailable') }}
  </ResponseBodyInfo>
</template>
<style scoped>
.light-mode .bg-preview {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23000' fill-opacity='10%25'%3E%3Crect width='8' height='8' /%3E%3Crect x='8' y='8' width='8' height='8' /%3E%3C/svg%3E");
}
.dark-mode .bg-preview {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23FFF' fill-opacity='10%25'%3E%3Crect width='8' height='8' /%3E%3Crect x='8' y='8' width='8' height='8' /%3E%3C/svg%3E");
}
</style>
